import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const RegionalOverviewMap = () => {
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [hoveredSite, setHoveredSite] = useState(null);

  const countries = [
  {
    id: 'serbia',
    name: 'Serbia',
    nameLocal: 'Србија',
    trailsCount: 4,
    heritageCount: 38,
    description: 'Explore ancient monasteries, medieval fortresses, and pristine national parks',
    flag: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763502563/srpska_zastava_slljwl.jpg",
    flagAlt: 'Serbian flag with red, blue and white horizontal stripes and coat of arms',
    mapPosition: { top: '35%', left: '65%' }
  },
  {
    id: 'montenegro',
    name: 'Montenegro',
    nameLocal: 'Црна Гора',
    trailsCount: 4,
    heritageCount: 39,
    description: 'Discover dramatic mountains, pristine coastline, and medieval towns',
    flag: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763502563/crnogorska_zastava_z81wq6.jpg",
    flagAlt: 'Montenegrin flag with red field and golden border with coat of arms',
    mapPosition: { top: '65%', left: '35%' }
  }];


  // Major heritage sites across both countries
  const heritageSites = [
  { id: 1, name: 'Studenica Monastery', country: 'serbia', position: { top: '40%', left: '60%' } },
  { id: 2, name: 'Golija National Park', country: 'serbia', position: { top: '45%', left: '55%' } },
  { id: 3, name: 'Tara River Canyon', country: 'montenegro', position: { top: '55%', left: '45%' } },
  { id: 4, name: 'Durmitor National Park', country: 'montenegro', position: { top: '50%', left: '30%' } },
  { id: 5, name: 'Biogradska Gora', country: 'montenegro', position: { top: '60%', left: '50%' } },
  { id: 6, name: 'Kopaonik National Park', country: 'serbia', position: { top: '50%', left: '70%' } }];


  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-foreground mb-4">
            Regional Overview
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Two neighboring countries united by shared heritage, natural beauty, and cultural traditions. 
            Explore cross-border trails that showcase the best of the Balkans.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Interactive Map */}
          <div className="relative">
            <div className="relative bg-card rounded-2xl shadow-lg p-8 overflow-hidden">
              <div className="aspect-square relative bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl">
                {/* Updated Map Background - Geographic map of Serbia and Montenegro */}
                <Image
                  src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763512114/mapa_r2hoyv.png"
                  alt="Detailed geographical map showing Serbia and Montenegro with borders, cities, mountains, and rivers clearly marked"
                  className="w-full h-full object-cover rounded-xl opacity-40" />

                
                {/* Heritage Site Markers */}
                {heritageSites?.map((site) =>
                <button
                  key={site?.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
                  style={site?.position}
                  onMouseEnter={() => setHoveredSite(site?.id)}
                  onMouseLeave={() => setHoveredSite(null)}>

                    <div className={`relative transition-all duration-300 ${
                  hoveredSite === site?.id ? 'scale-125' : 'scale-100'}`
                  }>
                      <div className="w-6 h-6 bg-secondary rounded-full flex items-center justify-center shadow-md border-2 border-white">
                        <Icon name="Mountain" size={12} color="white" />
                      </div>
                      
                      {/* Site Info Tooltip */}
                      {hoveredSite === site?.id &&
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-card rounded-lg shadow-xl p-3 border border-border z-20">
                          <h4 className="font-semibold text-xs text-foreground">{site?.name}</h4>
                          <p className="text-xs text-muted-foreground capitalize">{site?.country}</p>
                        </div>
                    }
                    </div>
                  </button>
                )}
                
                {/* Country Markers */}
                {countries?.map((country) =>
                <button
                  key={country?.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
                  style={country?.mapPosition}
                  onMouseEnter={() => setHoveredCountry(country?.id)}
                  onMouseLeave={() => setHoveredCountry(null)}>

                    <div className={`relative transition-all duration-300 ${
                  hoveredCountry === country?.id ? 'scale-110' : 'scale-100'}`
                  }>
                      <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                        <Icon name="MapPin" size={24} color="white" />
                      </div>
                      
                      {/* Country Info Popup */}
                      {hoveredCountry === country?.id &&
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-card rounded-lg shadow-xl p-4 border border-border z-10">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-6 rounded overflow-hidden">
                              <Image
                            src={country?.flag}
                            alt={country?.flagAlt}
                            className="w-full h-full object-cover" />

                            </div>
                            <div>
                              <h3 className="font-semibold text-sm">{country?.name}</h3>
                              <p className="text-xs text-muted-foreground">{country?.nameLocal}</p>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mb-3">{country?.description}</p>
                          <div className="flex justify-between text-xs">
                            <span>{country?.trailsCount} Trails</span>
                            <span>{country?.heritageCount} Sites</span>
                          </div>
                        </div>
                    }
                    </div>
                  </button>
                )}
              </div>
              
              {/* Map Legend */}
              <div className="absolute bottom-4 left-4 bg-card rounded-lg shadow-md p-3 border border-border">
                <h4 className="font-semibold text-xs text-foreground mb-2">Legend</h4>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-accent rounded-full flex items-center justify-center">
                      <Icon name="MapPin" size={8} color="white" />
                    </div>
                    <span className="text-xs text-muted-foreground">Countries</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-secondary rounded-full flex items-center justify-center">
                      <Icon name="Mountain" size={8} color="white" />
                    </div>
                    <span className="text-xs text-muted-foreground">Heritage Sites</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Country Statistics */}
          <div className="space-y-6">
            {countries?.map((country) =>
            <div key={country?.id} className="bg-card rounded-xl shadow-card p-6 card-hover">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-8 rounded overflow-hidden flex-shrink-0">
                    <Image
                    src={country?.flag}
                    alt={country?.flagAlt}
                    className="w-full h-full object-cover" />

                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-heading font-semibold text-lg">{country?.name}</h3>
                      <span className="text-sm text-muted-foreground">({country?.nameLocal})</span>
                    </div>
                    <p className="text-muted-foreground text-sm mb-4">{country?.description}</p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-3 bg-surface rounded-lg">
                        <div className="font-bold text-xl text-primary">{country?.trailsCount}</div>
                        <div className="text-xs text-muted-foreground">Thematic Trails</div>
                      </div>
                      <div className="text-center p-3 bg-surface rounded-lg">
                        <div className="font-bold text-xl text-accent">{country?.heritageCount}</div>
                        <div className="text-xs text-muted-foreground">Heritage Sites</div>
                      </div>
                    </div>
                    
                    <Link to="/thematic-trails-overview">
                      <Button variant="outline" size="sm" className="w-full">
                        <Icon name="ArrowRight" size={16} />
                        <span className="ml-2">Explore {country?.name}</span>
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default RegionalOverviewMap;